import {Injectable, OnDestroy} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, Observable, combineLatest, of} from 'rxjs';
import {map, distinctUntilChanged, shareReplay, switchMap} from 'rxjs/operators';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {AuthService} from '@mydocma/auth';
import {AnnoTypeEnum} from '@mydocma/component-library/src/lib/annotation';

export interface AnnotationPermissions {
  create?: boolean;
  update?: boolean;

  delete?: boolean;
}

export interface AttachmentPermissions {
  annotations?: AnnotationPermissions;
}

@UntilDestroy()
@Injectable({providedIn: 'root'})
export class AnnotationPermissionsService implements OnDestroy {

  private currentUser: User;
  private annotationPermissionsSubject = new BehaviorSubject<AnnotationPermissions>(null);

  public permissionCreate$: Observable<boolean>;
  public permissionUpdate$: Observable<boolean>;
  public permissionDelete$: Observable<boolean>;

  constructor(
    private readonly authService: AuthService,
    private readonly projectLoaderService: MmProjectLoaderService
  ) {
    this.initializePermissions();
    this.subscribeToCurrentUser();
    this.subscribeToAuthPermissions();
  }

  ngOnDestroy(): void {
  }

  private initializePermissions(): void {
    this.permissionCreate$ = this.annotationPermissionsSubject.pipe(
      map(annoPerms => !!annoPerms?.create),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.permissionUpdate$ = this.annotationPermissionsSubject.pipe(
      map(annoPerms => !!annoPerms?.update),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.permissionDelete$ = this.annotationPermissionsSubject.pipe(
      map(annoPerms => !!annoPerms?.delete),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  private subscribeToCurrentUser(): void {
    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this))
      .subscribe(userPerms => {
        if (userPerms.loadState.status === LoadStatusEnum.Loaded) {
          this.currentUser = userPerms.data.User;
        }
      });
  }

  private subscribeToAuthPermissions(): void {
    this.authService.userPermissions
      .pipe(untilDestroyed(this))
      .subscribe((p: any) => {
        const annotationPerms = p?.project?.attachments?.annotations;
        this.annotationPermissionsSubject.next(annotationPerms);
      });
  }

  public canStartAnnotationMode(): Observable<boolean> {
    return this.permissionCreate$;
  }

  public canUpdateAnnotation(annotation: any): Observable<boolean> {
    return this.permissionCreate$.pipe(
      switchMap(hasCreate => {
        const isOwn = this.isOwnAnnotation(annotation);
        if (hasCreate && isOwn) {
          return of(true);
        }
        return this.permissionUpdate$;
      })
    );
  }

  public canDeleteAnnotation(annotation: any): Observable<boolean> {
    return this.permissionCreate$.pipe(
      switchMap(hasCreate => {
        const isOwn = this.isOwnAnnotation(annotation);
        if (hasCreate && isOwn) {
          return of(true);
        }
        return this.permissionDelete$;
      })
    );
  }

  public getAnnotationPermissions(annotation: any): Observable<{edit: boolean, delete: boolean, move: boolean, resizable: boolean}> {
    return combineLatest([
      this.permissionCreate$,
      this.permissionUpdate$,
      this.permissionDelete$
    ]).pipe(
      map(([hasCreate, hasUpdate, hasDelete]) => {
        const isOwn = this.isOwnAnnotation(annotation);
        return {
          edit: hasUpdate || (hasCreate && isOwn),
          delete: hasDelete || (hasCreate && isOwn),
          move: hasUpdate || (hasCreate && isOwn),
          resizable: hasUpdate || (hasCreate && isOwn)
        };
      })
    );
  }


  public isOwnAnnotation(annotation: any, currentUser?: User): boolean {
    const user = currentUser || this.currentUser;

    if (!annotation || !user) {
      return false;
    }

    if (annotation.Creator?.Id && user.Id) {
      if (annotation.Creator.Id === user.Id) {
        return true;
      }
    }

    return false;
  }

  public isEditableAnnotationType(type: AnnoTypeEnum): boolean {
    return type === AnnoTypeEnum.Text ||
           type === AnnoTypeEnum.Arrow ||
           type === AnnoTypeEnum.Pen ||
           type === AnnoTypeEnum.Marker;
  }
}
